/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { SkillTag } from '@coursera/grpc-types-skills/coursera/proto/skills/tags/v2/skill_tag';

import EnterpriseSkillPathwayV2Loading from 'bundles/enterprise-learning-path-description-page/components/EnterpriseSkillPathwayV2Loading';
import EnterpriseLearningPathBreadcrumbsModuleRenderer from 'bundles/enterprise-learning-path-description-page/module-renderer/EnterpriseLearningPathBreadcrumbsModuleRenderer';
import EnterpriseOrgScopedLearningPathBreadcrumbsModuleRenderer from 'bundles/enterprise-learning-path-description-page/module-renderer/EnterpriseOrgScopedLearningPathBreadcrumbsModuleRenderer';
import EnterpriseSkillPathwayProductOverviewModuleRenderer from 'bundles/enterprise-learning-path-description-page/module-renderer/EnterpriseSkillPathwayProductOverviewModuleRenderer';
import EnterpriseSkillPathwaySyllabusModuleRenderer from 'bundles/enterprise-learning-path-description-page/module-renderer/EnterpriseSkillPathwaySyllabusModuleRenderer';
import type { EnterpriseLearningPathPageFieldsFragment } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseLearningPathPageFragments';
import type { QueryVerifiedSkillPathsPageQuery } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/GetSkillPathwayQueryV2';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';
import { ModuleNames, type PageConfig } from 'bundles/unified-description-page-common/types/moduleConfig';

const styles = {
  container: css`
    padding-bottom: 36px;
  `,
};

type Props = {
  pageData: EnterpriseLearningPathPageFieldsFragment;
  skillTagsData: Record<string, SkillTag[]>;
  pageConfig?: PageConfig | null;
  vspPageV2Data?: QueryVerifiedSkillPathsPageQuery;
  vspPageV2DataLoading?: boolean;
};

const Renderer = ({ pageData, skillTagsData, pageConfig, vspPageV2Data, vspPageV2DataLoading }: Props) => {
  const isOrgScopedHome = useIsOrgScopedHome();

  if (!pageConfig || !pageConfig.modules.length || !skillTagsData) {
    return null;
  }

  const vspPageV2DataArray = vspPageV2Data?.DescriptionPage?.queryVerifiedSkillPathsPage;

  const modulesToRender = pageConfig.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        return (
          <EnterpriseSkillPathwayProductOverviewModuleRenderer pageData={pageData} vspPageV2Data={vspPageV2DataArray} />
        );
      case ModuleNames.Breadcrumbs:
        return isOrgScopedHome ? (
          <EnterpriseOrgScopedLearningPathBreadcrumbsModuleRenderer pageData={pageData} />
        ) : (
          <EnterpriseLearningPathBreadcrumbsModuleRenderer pageData={pageData} />
        );
      case ModuleNames.Syllabus:
        if (vspPageV2DataLoading) {
          return <EnterpriseSkillPathwayV2Loading />;
        }
        return <EnterpriseSkillPathwaySyllabusModuleRenderer pageData={pageData} vspPageV2Data={vspPageV2DataArray} />;
      default:
        return null;
    }
  });
  return <div css={styles.container}>{modulesToRender}</div>;
};

export default Renderer;
